import { PaginatedPayload, WorkflowStatus, PaginationInfo, PaginatedResult } from './../../models/util.interface';
import { AppSettings } from '@/config/app-settings';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import axios from 'axios';
import { User } from '@/models/user.interface';

@Module
export default class UserStore extends VuexModule {
  /* tslint:disable */
  private _users: Array<User> = new Array();
  private _loading: boolean = false;
  private _selectedUser: User | null = null;
  private _page: number = 1;
  private _paginationInfo: PaginationInfo = { limit: 10, pages: 0, page: "0", total: 0 };
  private _addSuccess: boolean = false;
  private _addError: string | null = null;
  private _updateSuccess: boolean = false;
  private _updateError: string | null = null;
  /* tslint:enable */

  @Mutation public setUsersMut(result: PaginatedResult<User[]>): void {
    this._users = result.data;
    this._paginationInfo = { limit: result.limit, pages: result.pages, page: result.page, total: result.total };
  }

  @Mutation public selectUser(user: User): void {
    this._selectedUser = user;
  }

  @Mutation public setUsersLoading(loading: boolean) {
    this._loading = loading;
  }

  @Mutation public addUserMut(user: User): void {
    if (user) {
      this._addSuccess = true;
      this._addError = null;
      this._users.push(user);
    } else {
      this._addSuccess = false;
      this._addError = this._addError ? this._addError : AppSettings.DEFAULT_ERROR;
    }
  }

  @Mutation public updateUserMut(user: User): void {
    if (user) {
      this._updateSuccess = true;
      this._updateError = null;
      this._users[this._users.findIndex((u: User) => u._id === user._id)] = user;
    } else {
      this._updateSuccess = false;
      this._updateError = this._updateError ? this._updateError : AppSettings.DEFAULT_ERROR;
    }
  }

  @Mutation public removeUserMut(id: string): void {
    if (id) {
      this._users = this._users.filter((u: User) => u._id !== id);
    }
  }

  @Mutation public updatePageUserMut(page: number): void {
    this._page = page;
  }

  @Mutation public resetUserWorkflowStatus(): void {
    this._addSuccess = false;
    this._addError = null;
    this._updateSuccess = false;
    this._updateError = null;
  }

  @Mutation public setUserAddError(error: string): void {
    this._addSuccess = false;
    this._addError = error;
  }

  @Action({ commit: 'setUsersMut' }) public async getUsersAction(
    payload?: PaginatedPayload,
  ): Promise<PaginatedResult<User[]>> {
    try {
      this.context.commit('setUsersLoading', true);
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/user`, { params: payload });
      this.context.commit('setUsersLoading', false);
      if (response.data.docs) {
        return {
          data: response.data.docs,
          limit: response.data.limit,
          pages: response.data.pages,
          page: response.data.page,
          total: response.data.total,
        };
      } else {
        return { data: [] };
      }
    } catch (err) {
      this.context.commit('setUsersLoading', false);
      return { data: [] };
    }
  }

  @Action({ commit: 'selectUser' }) public async getUserAction(id: string): Promise<User | null> {
    try {
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/user/${id}`);
      return response.data;
    } catch (err) {
      return null;
    }
  }

  @Action({ commit: 'addUserMut' }) public async addUserAction(user: User): Promise<User | null> {
    try {
      this.context.commit('resetUserWorkflowStatus');
      await axios.post(`${process.env.VUE_APP_API_URL}/user`, { ...user });
      return user;
    } catch (err) {
      err.response.data.msg && this.context.commit('setUserAddError', err.response.data.msg);
      return null;
    }
  }

  @Action({ commit: 'updateUserMut' }) public async updateUserAction(user: User): Promise<User | null> {
    try {
      this.context.commit('resetUserWorkflowStatus');
      await axios.put(`${process.env.VUE_APP_API_URL}/user/${user._id}`, { ...user });
      return user;
    } catch (err) {
      return null;
    }
  }

  @Action({ commit: 'selectUser' }) public async selectUserAction(user: User): Promise<User | null> {
    return user;
  }

  @Action({ commit: 'removeUserMut' }) public async removeUserAction(id: string) {
    try {
      await axios.delete(`${process.env.VUE_APP_API_URL}/user/${id}`);
      return id;
    } catch (err) {
      return null;
    }
  }

  @Action({ commit: 'updatePageUserMut' }) public async changeUserPageAction(page: number): Promise<number | null> {
    return page;
  }

  @Action({ commit: 'resetUserWorkflowStatus' }) public resetUserWorkflowAction(): void {
    return;
  }

  get users(): User[] {
    return this._users;
  }

  get usersLoading(): boolean {
    return this._loading;
  }

  get selectedUser(): User | null {
    return this._selectedUser;
  }

  get userPage(): number {
    return this._page;
  }

  get userPaginationInfo(): PaginationInfo {
    return this._paginationInfo;
  }

  get userWorkflowStatus(): WorkflowStatus {
    return {
      add: {
        success: this._addSuccess,
        error: this._addError,
      },
      update: {
        success: this._updateSuccess,
        error: this._updateError,
      },
    };
  }
}
